import { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import type { Rule, CreateRuleRequest, UpdateRuleRequest, Tier } from '@mockd/shared';
import type { RuleExport } from '@mockd/shared/types/rule';
import { api, ApiError } from '../utils/apiClient';
import { toAppendableRules } from '../utils/exportEndpoint';

/** What GET .../rules returns: the rules plus the cap the server enforces on them. */
interface RuleListResponse {
  data: Rule[];
  ruleVersion?: string;
  tier?: Tier;
  limit?: number;
}

interface UseRulesReturn {
  rules: Rule[];
  loading: boolean;
  error: string | null;
  /** Rules allowed per endpoint on the project's effective tier; null until loaded. */
  ruleLimit: number | null;
  /** The project's effective tier — not the viewer's; null until loaded. */
  tier: Tier | null;
  fetchRules: (projectId: string, endpointId: string) => Promise<void>;
  createRule: (projectId: string, endpointId: string, data: Omit<CreateRuleRequest, 'endpointId'>) => Promise<Rule>;
  updateRule: (projectId: string, endpointId: string, ruleId: string, data: UpdateRuleRequest) => Promise<Rule>;
  deleteRule: (projectId: string, endpointId: string, ruleId: string) => Promise<void>;
  reorderRules: (projectId: string, endpointId: string, ruleIds: string[]) => Promise<Rule[]>;
  duplicateRule: (projectId: string, endpointId: string, ruleId: string) => Promise<Rule>;
  resetRuleSequence: (projectId: string, endpointId: string, ruleId: string) => Promise<void>;
  exportRules: (projectId: string, endpointId: string) => Promise<RuleExport[]>;
  importRules: (projectId: string, endpointId: string, rules: RuleExport[]) => Promise<void>;
}

export function useRules(): UseRulesReturn {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ruleLimit, setRuleLimit] = useState<number | null>(null);
  const [tier, setTier] = useState<Tier | null>(null);
  const ruleVersionRef = useRef<string | undefined>(undefined);

  /**
   * Adopt a rules-list response. Every mutation below refetches the list, so routing
   * them all through here keeps the count, version, tier, and cap in step.
   */
  const applyList = useCallback((json: RuleListResponse) => {
    setRules(json.data || []);
    if (json.ruleVersion) ruleVersionRef.current = json.ruleVersion;
    if (json.limit !== undefined) setRuleLimit(json.limit);
    if (json.tier) setTier(json.tier);
  }, []);

  const listRules = useCallback((projectId: string, endpointId: string) =>
    api.get<RuleListResponse>(`/api/projects/${projectId}/endpoints/${endpointId}/rules`), []);

  const fetchRules = useCallback(async (projectId: string, endpointId: string) => {
    setLoading(true);
    setError(null);
    try {
      applyList(await listRules(projectId, endpointId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  }, [applyList, listRules]);

  const createRule = useCallback(async (
    projectId: string,
    endpointId: string,
    data: Omit<CreateRuleRequest, 'endpointId'>
  ): Promise<Rule> => {
    try {
      const json = await api.post<{ data: Rule }>(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules`,
        data
      );
      // Refetch to get updated version
      applyList(await listRules(projectId, endpointId));
      toast.success('Rule created');
      return json.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create rule';
      // Another tab may have taken the last slot — resync so the panel reflects the cap
      // it just hit rather than offering an Add Rule button that will fail again.
      if (err instanceof ApiError && err.code === 'RULE_LIMIT_EXCEEDED') {
        await listRules(projectId, endpointId).then(applyList).catch(() => {});
      }
      toast.error(message);
      throw err;
    }
  }, [applyList, listRules]);

  const updateRule = useCallback(async (
    projectId: string,
    endpointId: string,
    ruleId: string,
    data: UpdateRuleRequest
  ): Promise<Rule> => {
    try {
      const json = await api.put<{ data: Rule }>(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules/${ruleId}`,
        data
      );
      setRules(prev => prev.map(r => r.id === ruleId ? json.data : r));
      toast.success('Rule updated');
      return json.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update rule';
      toast.error(message);
      throw err;
    }
  }, []);

  const deleteRule = useCallback(async (
    projectId: string,
    endpointId: string,
    ruleId: string
  ): Promise<void> => {
    try {
      await api.del(`/api/projects/${projectId}/endpoints/${endpointId}/rules/${ruleId}`);
      setRules(prev => prev.filter(r => r.id !== ruleId));
      // Update version after delete
      ruleVersionRef.current = undefined;
      applyList(await listRules(projectId, endpointId));
      toast.success('Rule deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete rule';
      toast.error(message);
      throw err;
    }
  }, [applyList, listRules]);

  const reorderRules = useCallback(async (
    projectId: string,
    endpointId: string,
    ruleIds: string[]
  ): Promise<Rule[]> => {
    try {
      const json = await api.patch<{ data: Rule[]; ruleVersion?: string }>(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules/reorder`,
        { ruleIds, ruleVersion: ruleVersionRef.current }
      );
      setRules(json.data);
      if (json.ruleVersion) ruleVersionRef.current = json.ruleVersion;
      return json.data;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Conflict — refresh rules to get current state
        toast.error('Rules were modified in another tab — refreshing');
        const listJson = await listRules(projectId, endpointId);
        applyList(listJson);
        return listJson.data || [];
      }
      const message = err instanceof ApiError ? err.message : 'Failed to reorder rules';
      toast.error(message);
      throw err;
    }
  }, [applyList, listRules]);

  const duplicateRule = useCallback(async (
    projectId: string,
    endpointId: string,
    ruleId: string
  ): Promise<Rule> => {
    try {
      const json = await api.post<{ data: Rule }>(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules/${ruleId}/duplicate`,
        {}
      );
      // Refetch to get updated priorities and version
      applyList(await listRules(projectId, endpointId));
      toast.success('Rule duplicated');
      return json.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to duplicate rule';
      if (err instanceof ApiError && err.code === 'RULE_LIMIT_EXCEEDED') {
        await listRules(projectId, endpointId).then(applyList).catch(() => {});
      }
      toast.error(message);
      throw err;
    }
  }, [applyList, listRules]);

  const resetRuleSequence = useCallback(async (
    projectId: string,
    endpointId: string,
    ruleId: string
  ): Promise<void> => {
    try {
      await api.post(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules/${ruleId}/reset`,
        {}
      );
      toast.success('Sequence position reset');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to reset sequence';
      toast.error(message);
      throw err;
    }
  }, []);

  const exportRules = useCallback(async (
    projectId: string,
    endpointId: string
  ): Promise<RuleExport[]> => {
    try {
      const json = await api.get<{ data: RuleExport[] }>(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules/export`
      );
      return json.data || [];
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to export rules';
      toast.error(message);
      throw err;
    }
  }, []);

  const importRules = useCallback(async (
    projectId: string,
    endpointId: string,
    imported: RuleExport[]
  ): Promise<void> => {
    try {
      await api.post(
        `/api/projects/${projectId}/endpoints/${endpointId}/rules/import`,
        { rules: toAppendableRules(imported) }
      );
      applyList(await listRules(projectId, endpointId));
      toast.success(`Imported ${imported.length} rule${imported.length === 1 ? '' : 's'}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to import rules';
      if (err instanceof ApiError && err.code === 'RULE_LIMIT_EXCEEDED') {
        await listRules(projectId, endpointId).then(applyList).catch(() => {});
      }
      toast.error(message);
      throw err;
    }
  }, [applyList, listRules]);

  return {
    rules,
    loading,
    error,
    ruleLimit,
    tier,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    reorderRules,
    duplicateRule,
    resetRuleSequence,
    exportRules,
    importRules,
  };
}
